import React, { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { PinOutline } from '@lyearn/icons';
import { cn, css } from '@lyearn/style';
import {
  Input,
  Popper,
  PopperProps,
  RenderInputParams,
  Select,
  Stack,
  Typography,
} from '@lyearn/ui';
import { renderMediumOptionLabel } from '@/components/Form/components/Select/optionLabelRenderer';
import { FormSelectOption } from '@/components/Form/components/Select/types';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useHomeEditorContext } from '@/modules/corporate/modules/home/views/Editor/context/HomeEditor';
import { LanguageEnum } from '@/types/schema';
interface SelectLanguageProps {
  placeholder?: string;
}
const RenderValue = <T,>({ Icon, label }: FormSelectOption<T>) => (
  <Stack alignItems="center" className="flex-1" gapX={6}>
    {Icon ? <Icon className="shrink-0 text-icon-primary w-16 h-16" /> : null}
    <Typography className="truncate" color="text-text-secondary" variant="body-short-02">
      {label}
    </Typography>
  </Stack>
);
const CLASSES = {
  paper: 'rounded-8 shadow-01 border border-divider my-8 w-[20rem] max-h-[15rem] overflow-hidden',
};
export const inputClasses = css`
  .MuiOutlinedInput-root.MuiAutocomplete-inputRoot {
    padding-inline-end: 0.4rem !important;
  }
`;
const renderInput = (params: RenderInputParams) => (
  <Input
    {...params}
    endAdornment={<PinOutline className="h-12 w-24" fillColorToken="icon-placeholder" />}
  />
);
export const getLanguageName = (language: LanguageEnum): string => {
  return language.charAt(0).toUpperCase() + language.slice(1).toLowerCase();
};

const LanguageMenu: React.FC<SelectLanguageProps> = ({ placeholder }) => {
  const { t } = useTranslation();

  const useSafeHomeEditorContext = (): {
    selectedLanguage?: LanguageEnum;
    setSelectedLanguage?: React.Dispatch<React.SetStateAction<LanguageEnum>>;
  } => {
    try {
      return useHomeEditorContext();
    } catch (e) {
      return {};
    }
  };

  const { selectedLanguage, setSelectedLanguage } = useSafeHomeEditorContext();

  const AppConfig = useAppConfig();
  const supportedLanguages = AppConfig?.site?.supportedLanguages;
  const onChange = (value: any, option: any) => {
    setSelectedLanguage?.(value);
  };

  const getOptionLabel = React.useCallback((item: any) => item.label, []);
  const options = useMemo(() => {
    const options = supportedLanguages?.map((language: any) => ({
      value: language,
      label: getLanguageName(language),
    }));

    return options ?? [];
  }, []);

  const POPPER_STYLE = { minWidth: 200, maxWidth: 220, minHieght: 50 };

  const PopperComponent = (props: PopperProps) => (
    <Popper {...props} placement="bottom-start" style={POPPER_STYLE} />
  );
  const currentLanguage = !!selectedLanguage ? selectedLanguage : LanguageEnum.English;

  return (
    <Select
      disableClearable
      className={cn('w-[15rem] xs:ml-8', inputClasses)}
      getOptionLabel={getOptionLabel}
      optionLabelRenderer={renderMediumOptionLabel}
      options={options}
      placeholder={placeholder ?? t('Common:Actions.ChooseLanguage')}
      renderValue={RenderValue}
      onChange={onChange}
      disabled={false}
      // classes={CLASSES}
      PopperComponent={PopperComponent}
      size="medium"
      // renderInput={renderInput}
      value={currentLanguage}
    />
  );
};

export default LanguageMenu;
